import { useState, useEffect } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { MapPin, Filter, Search, CheckCircle, Clock, Users, X } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import {
  isProjectCategorySlug,
  PROJECTS_FILTERS_ID,
  PROJECTS_STICKY_TOP_CLASS,
  scheduleScrollToProjectsFilters,
} from '../constants/projects';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  ongoing: { label: 'Ongoing', color: 'bg-amber-500 text-white', icon: Clock },
  completed: { label: 'Completed', color: 'bg-countryside-green text-white', icon: CheckCircle },
  volunteer_needed: { label: 'Volunteers Needed', color: 'bg-countryside-terracotta text-white', icon: Users },
};

const categories = [
  { value: 'all', label: 'All Categories' },
  { value: 'habitat', label: 'Habitat Restoration' },
  { value: 'trails', label: 'Access & Trails' },
  { value: 'farming', label: 'Farming Partnerships' },
  { value: 'centenary', label: 'Centenary Projects' },
];

const counties = [
  { value: 'all', label: 'All Counties' },
  { value: 'Rutland', label: 'Rutland' },
  { value: 'Lincolnshire', label: 'Lincolnshire' },
];

const statuses = [
  { value: 'all', label: 'All Status' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'completed', label: 'Completed' },
  { value: 'volunteer_needed', label: 'Volunteers Needed' },
];

const ProjectsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  const categoryFilter = searchParams.get('category') || 'all';
  const countyFilter = searchParams.get('county') || 'all';
  const statusFilter = searchParams.get('status') || 'all';

  useEffect(() => {
    if (isProjectCategorySlug(categoryFilter)) {
      return scheduleScrollToProjectsFilters({ delayMs: 50 });
    }
    if (location.hash === '#projects-filters') {
      return scheduleScrollToProjectsFilters({ delayMs: 50 });
    }
  }, [categoryFilter, location.pathname, location.search, location.hash]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data || []);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
        setProjects([
          {
            id: '1',
            title: 'Rutland Water Habitat Restoration',
            description: 'A comprehensive habitat restoration project at Rutland Water, focusing on wetland conservation.',
            location: 'Rutland Water Nature Reserve',
            county: 'Rutland',
            category: 'habitat',
            status: 'ongoing',
            image_url: '/images/cpre images for website/17.jpg',
          },
          {
            id: '2',
            title: 'Lincoln Wolds Trail Enhancement',
            description: 'Improving public access along the Lincoln Wolds with new waymarkers and information boards.',
            location: 'Lincoln Wolds',
            county: 'Lincolnshire',
            category: 'trails',
            status: 'ongoing',
            image_url: '/images/cpre images for website/13.jpg',
          },
          {
            id: '3',
            title: 'Centenary Oak Planting',
            description: 'Celebrating our 100th year with the planting of 100 native oak trees.',
            location: 'Various Locations',
            county: 'Rutland',
            category: 'centenary',
            status: 'volunteer_needed',
            image_url: '/images/cpre images for website/22.png',
          },
          {
            id: '4',
            title: 'Sustainable Farming Partnership',
            description: 'Working with local farmers to implement sustainable farming practices.',
            location: 'South Lincolnshire',
            county: 'Lincolnshire',
            category: 'farming',
            status: 'completed',
            image_url: '/images/cpre images for website/2.jpg',
          },
          {
            id: '5',
            title: 'Opposing Overhead Pylon Infrastructure',
            description:
              'Campaigning against proposed overhead pylon lines and substations across Lincolnshire linked to North Sea offshore wind connections, while pressing for underground cabling and landscape-sensitive alternatives.',
            location: 'Lincolnshire',
            county: 'Lincolnshire',
            category: 'planning',
            status: 'ongoing',
            image_url: '/images/cpre images for website/11.jpg',
          },
          {
            id: '6',
            title: 'Solar Farm Agricultural Land Protection',
            description:
              'Opposing the loss of good-quality agricultural land to large-scale solar farms and promoting rooftop solar and brownfield sites as sustainable alternatives across the county.',
            location: 'Lincolnshire',
            county: 'Lincolnshire',
            category: 'planning',
            status: 'ongoing',
            image_url: '/images/cpre images for website/25.jpg',
          },
          {
            id: '7',
            title: 'Lincolnshire Wolds Landscape Preservation',
            description:
              'Protecting the chalk downlands, heathlands, and historic landscapes of the Lincolnshire Wolds from inappropriate development and supporting projects that enhance countryside character.',
            location: 'Lincolnshire Wolds',
            county: 'Lincolnshire',
            category: 'habitat',
            status: 'ongoing',
            image_url: '/images/cpre images for website/13.jpg',
          },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearch('');
  };

  const filteredProjects = projects.filter((p) => {
    if (categoryFilter !== 'all' && p.category !== categoryFilter) return false;
    if (countyFilter !== 'all' && p.county !== countyFilter) return false;
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (search !== '') {
      const q = search.toLowerCase();
      const matchesTitle = p.title?.toLowerCase().includes(q);
      const matchesLocation = p.location?.toLowerCase().includes(q);
      if (!matchesTitle && !matchesLocation) return false;
    }
    return true;
  });

  const hasActiveFilters = categoryFilter !== 'all' || countyFilter !== 'all' || statusFilter !== 'all' || search !== '';

  return (
    <Layout>
      <div data-testid="projects-page">
        {/* Hero Section */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-countryside-green">
          <div className="absolute inset-0 opacity-20">
            <img
              src="/images/cpre images for website/11.jpg"
              alt="Countryside"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative container-custom text-center text-white">
            <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4 text-white">Our Projects</h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Discover how we're actively protecting and enhancing the countryside across Rutland and Lincolnshire.
            </p>
          </div>
        </section>

        {/* Filters */}
        <div
          id={PROJECTS_FILTERS_ID}
          className="scroll-mt-[6.5rem] md:scroll-mt-[7.5rem]"
        >
          <div
            className={`sticky ${PROJECTS_STICKY_TOP_CLASS} z-40 bg-white border-b border-stone-200 shadow-sm`}
          >
            <div className="container-custom py-4 md:py-5">
            <div
              className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-center justify-between"
              role="toolbar"
              aria-label="Filter projects"
            >
              <div className="flex flex-wrap gap-3 w-full lg:w-auto min-w-0">
                <Select value={categoryFilter} onValueChange={(v) => updateFilter('category', v)}>
                  <SelectTrigger className="w-[180px] rounded-lg" data-testid="filter-category">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={countyFilter} onValueChange={(v) => updateFilter('county', v)}>
                  <SelectTrigger className="w-[160px] rounded-lg" data-testid="filter-county">
                    <SelectValue placeholder="County" />
                  </SelectTrigger>
                  <SelectContent>
                    {counties.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={(v) => updateFilter('status', v)}>
                  <SelectTrigger className="w-[180px] rounded-lg" data-testid="filter-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground" data-testid="clear-filters">
                    <X className="w-4 h-4 mr-1" /> Clear
                  </Button>
                )}
              </div>

              <div className="relative w-full lg:w-80 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search projects..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 rounded-lg w-full"
                  data-testid="search-projects"
                />
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* Projects Grid */}
        <section className="section-padding pt-8 md:pt-10">
          <div className="container-custom">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="bg-stone-100 rounded-2xl h-96 animate-pulse" />
                ))}
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-muted-foreground text-lg">No projects found matching your criteria.</p>
                <Button onClick={clearFilters} className="mt-4" variant="outline">
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProjects.map((project, index) => {
                  const status = statusConfig[project.status] || statusConfig.ongoing;
                  const StatusIcon = status.icon;

                  return (
                    <Link key={project.id} to={`/projects/${project.id}`}>
                      <Card 
                        className="bg-white border-stone-200 overflow-hidden group h-full hover:shadow-lg transition-shadow duration-300"
                        data-testid={`project-card-${index}`}
                      >
                        <div className="aspect-video overflow-hidden relative">
                          <img
                            src={project.image_url || '/images/cpre images for website/17.jpg'}
                            alt={project.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <Badge className={`${status.color} absolute top-4 left-4 rounded-full text-xs font-medium`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                        </div>
                        <CardContent className="p-6">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                            <MapPin className="w-4 h-4 text-countryside-green" />
                            {project.location}
                          </div>
                          <h3 className="font-heading font-semibold text-xl text-foreground mb-2 group-hover:text-countryside-green transition-colors duration-200">
                            {project.title}
                          </h3>
                          <p className="text-muted-foreground text-sm line-clamp-2">
                            {project.description}
                          </p>
                          <Badge variant="outline" className="mt-4 capitalize">
                            {project.county}
                          </Badge>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default ProjectsPage;
